import React from 'react';
import { Select } from '../../../CommonComponents/OPL/Select/Select/Select';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Icon } from '../../../CommonComponents/OPL/Icon/Icon';
import { useLandingPage } from './useLandingPage';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { SELECT_WIDTH } from './LandingPage.meta';
import { Label } from '../ClientAccessSettings/components/Label/Label';
import { LandingPage as LandingPageEnum } from '../../ClientAccess.types';
import { MarginProps } from '../../../theme/styledProps';
import { OptionType } from '../../../CommonComponents/OPL/Select/BaseSelect/types';
import { DashboardFeatureId } from '../../../AdviserArea/AdviserArea.types';
import { HelperText } from '../../../CommonComponents/OPL/HelperText/HelperText';

interface Props extends MarginProps {
  displayName?: string;
  clientLandingPage: LandingPageEnum | null;
  defaultClientLandingPage: LandingPageEnum;
  clientLandingPageOptions: OptionType<LandingPageEnum>[];
  dashboardAccess: DashboardFeatureId[] | null;
  onChange({ clientLandingPage }: { clientLandingPage: LandingPageEnum | null }): void;
  hasOrionReportsEnabled?: boolean;
}

export const LandingPage: React.FC<Props> = ({
  displayName = 'client',
  clientLandingPage,
  defaultClientLandingPage,
  clientLandingPageOptions,
  onChange,
  dashboardAccess,
  hasOrionReportsEnabled,
  ...rest
}) => {
  const { defaultLandingPageName, filteredOptions, isDefaultLandingPageValid } = useLandingPage({
    defaultClientLandingPage,
    clientLandingPageOptions,
    dashboardAccess,
    landingPageOptions: clientLandingPageOptions,
    hasOrionReportsEnabled,
  });

  if (!dashboardAccess || dashboardAccess.length === 0) {
    return null;
  }

  return (
    <Box as="section" {...rest}>
      <Label htmlFor="select--ca-landing-page">Client Portal Landing Page</Label>

      <Box display="flex" justifyContent="space-between" alignItems="center" mt="16">
        <HelperText descriptionId="description--ca-landing-page" mr="24">
          Select the default landing page the user sees when logging in.
        </HelperText>

        <Box width={SELECT_WIDTH} flexShrink={0}>
          <Select
            id="ca-landing-page"
            value={filteredOptions
              .map(({ options }) => options)
              .flat()
              .find(({ value }) => value === clientLandingPage)}
            options={filteredOptions}
            onChange={opt => {
              if (opt) {
                onChange({ clientLandingPage: opt.value });
              }
            }}
            menuPlacement="top"
            aria-describedby="description--ca-landing-page"
          />
        </Box>
      </Box>

      {clientLandingPage === null && !isDefaultLandingPageValid && (
        <Box display="flex" alignItems="baseline" mt="4" width={320} ml="auto">
          <Icon
            className="far fa-exclamation-triangle"
            color="neutral.500"
            fontSize="small"
            mr="4"
          />

          <Text as="p" fontSize="small" color="neutral.500">
            {`This ${displayName} currently does not have access to ${defaultLandingPageName}`}. The
            landing page will default to Personal Finances - Summary.
          </Text>
        </Box>
      )}
    </Box>
  );
};
